import React, { Fragment, useEffect, useState } from "react";
import {
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Label,
  Row,
  Input,
} from "reactstrap";
import { Btn, H3, Breadcrumbs } from "../../../AbstractElements";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { updateUserAccount, getUserDetails } from "../../../CommonURL";
import {
  postDataWithToken,
  getDataWithToken,
} from "../../../Utils/restUtils";

import Transparentspinner from "../../Common/loader/Transparentspinner";

import Select from "react-select";
import { Country } from "country-state-city";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const schema = yup.object().shape({
  first_name: yup.string().required("First name is required"),
  last_name: yup.string().required("Last name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  country_code: yup.string().required("Country is required"),
  mobile: yup.string().required("Mobile number is required"),
});

const EditStaffForm = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      country_code: "",
      country: "",
      role_id: 4,
    },
  });

  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  useEffect(() => {
    fetchStaffDetails();
  }, []);

  const fetchStaffDetails = async () => {
    try {
      setLoading(true);

      const res = await getDataWithToken(`${getUserDetails}?user_id=${id}`);

      if (res?.status === true) {
        const staff = res.data;

        reset({
          first_name: staff.first_name || "",
          last_name: staff.last_name || "",
          email: staff.email || "",
          mobile: staff.mobile || "",
          country_code: staff.country_code || "",
          country: staff.country || "",
          role_id: 4,
        });

        const country = Country.getAllCountries().find(
          (c) => c.phonecode === String(staff.country_code)
        );

        if (country) {
          setSelectedCountry(country);
        }
      }
    } catch (error) {
      console.error(error);
    }

    setLoading(false);
  };

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      const requestBody = {
        ...data,
        user_id: id,
        role_id: 4,
      };

      const res = await postDataWithToken(
        updateUserAccount,
        requestBody
      );

      if (res?.data?.status === true) {
        navigate(`${process.env.PUBLIC_URL}/master-records/Staff`);
      } else {
        alert("Failed to update staff");
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    }

    setLoading(false);
  };

  return (
    <Fragment>
      {loading && <Transparentspinner />}

      <Breadcrumbs
        mainTitle="Edit Staff"
        parent="Administration"
        title="Edit Staff"
      />

      <Card>
        <CardHeader>
          <H3>Edit Staff</H3>
        </CardHeader>

        <CardBody>
          <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Row className="g-3">
              {/* First Name */}
              <Col md="6">
                <Label>
                  First Name <span className="requireStar">*</span>
                </Label>

                <input
                  className="form-control"
                  {...register("first_name")}
                />

                <span className="text-danger">
                  {errors.first_name?.message}
                </span>
              </Col>

              {/* Last Name */}
              <Col md="6">
                <Label>
                  Last Name <span className="requireStar">*</span>
                </Label>

                <input
                  className="form-control"
                  {...register("last_name")}
                />

                <span className="text-danger">
                  {errors.last_name?.message}
                </span>
              </Col>

              {/* Email */}
              <Col md="6">
                <Label>
                  Email <span className="requireStar">*</span>
                </Label>

                <input
                  type="email"
                  className="form-control"
                  {...register("email")}
                />

                <span className="text-danger">
                  {errors.email?.message}
                </span>
              </Col>

              {/* Role */}
              <Col md="6">
                <Label>
                  Role <span className="requireStar">*</span>
                </Label>

                <Controller
                  name="role_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={[{ label: "Staff", value: 4 }]}
                      value={{ label: "Staff", value: 4 }}
                      isDisabled
                    />
                  )}
                />
              </Col>

              {/* Country */}
              <Col md="6">
                <Label>
                  Country <span className="requireStar">*</span>
                </Label>

                <Controller
                  name="country_code"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={Country.getAllCountries()}
                      getOptionLabel={(opt) =>
                        `${opt.name} (+${opt.phonecode})`
                      }
                      getOptionValue={(opt) => opt.phonecode}
                      value={selectedCountry}
                      onChange={(item) => {
                        setSelectedCountry(item);
                        field.onChange(item.phonecode);
                        setValue("country", item.name);
                      }}
                    />
                  )}
                />

                <span className="text-danger">
                  {errors.country_code?.message}
                </span>
              </Col>

              {/* Mobile */}
              <Col md="6">
                <Label>
                  Mobile <span className="requireStar">*</span>
                </Label>

                <Controller
                  name="mobile"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      country={"in"}
                      value={field.value}
                      onChange={(value, country) => {
                        field.onChange(value);

                        setValue("country_code", country.dialCode);
                        setValue("country", country.name);
                      }}
                      enableSearch
                      inputStyle={{
                        width: "100%",
                        height: "38px",
                      }}
                    />
                  )}
                />

                <span className="text-danger">
                  {errors.mobile?.message}
                </span>
              </Col>
            </Row>

            <Btn
              attrBtn={{
                color: "primary",
                className: "mt-3",
              }}
              type="submit"
            >
              Update Staff
            </Btn>
          </Form>
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default EditStaffForm;